import React, { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';

/**
 * InstallPrompt - Prompts user to install Mabel as PWA
 * Shows only if:
 * - PWA is installable (browser supports it)
 * - User hasn't dismissed it
 * - App isn't already installed
 */
export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    // Check if user previously dismissed
    const dismissed = localStorage.getItem('mabel-install-dismissed');
    if (dismissed === 'true') {
      return;
    }

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return; // Already installed
    }

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      // Fallback for browsers that don't support beforeinstallprompt
      // Show manual instructions
      alert(
        'To install Mabel:\n\n' +
        'Desktop: Look for the install icon in your browser address bar\n\n' +
        'Android: Tap the menu (⋮) and select "Install app"\n\n' +
        'iOS: Tap Share (↑) and select "Add to Home Screen"'
      );
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User installed Mabel');
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    localStorage.setItem('mabel-install-dismissed', 'true');
    setShowPrompt(false);
  };

  if (!showPrompt) {
    return null;
  }

  return (
    <div style={{
      background: 'linear-gradient(135deg, #3D9A8B 0%, #2D8A7B 100%)',
      borderRadius: '16px',
      padding: '20px',
      color: 'white',
      position: 'relative',
      boxShadow: '0 4px 12px rgba(61, 154, 139, 0.2)'
    }}>
      {/* Dismiss button */}
      <button
        onClick={handleDismiss}
        style={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          background: 'rgba(255, 255, 255, 0.2)',
          border: 'none',
          borderRadius: '6px',
          width: '28px',
          height: '28px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          color: 'white'
        }}
        aria-label="Dismiss"
      >
        <X size={16} />
      </button>

      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
        {/* Icon */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.2)',
          borderRadius: '12px',
          width: '48px',
          height: '48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <Download size={24} />
        </div>

        {/* Content */}
        <div style={{ flex: 1, paddingRight: '32px' }}>
          <h3 style={{
            margin: '0 0 8px 0',
            fontSize: '18px',
            fontWeight: '600'
          }}>
            Install Mabel
          </h3>
          <p style={{
            margin: '0 0 16px 0',
            fontSize: '14px',
            lineHeight: '1.5',
            opacity: 0.95
          }}>
            Add Mabel to your home screen for quick access. Works offline and feels like a native app.
          </p>

          {/* Install button */}
          <button
            onClick={handleInstall}
            style={{
              background: 'white',
              color: '#3D9A8B',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
            }}
          >
            Install Now
          </button>
        </div>
      </div>
    </div>
  );
}
